import { Outlet, useLocation } from 'react-router-dom';
import { LanguageQueryProvider, SiteLink, SiteNavLink, useHydrated } from './site-link';
import { useTranslation } from 'react-i18next';
import {
  explicitLanguagePath,
  languageSwitchHref,
  localeFromPathname,
  localizePath,
  type Locale,
} from '@shared/locales';
import { useUrlLocale } from '../i18n/url-locale';
import { appLinks, buildConfig } from '../lib/config';
import { getSettings } from '../lib/content';
import { Wordmark } from './logo';
import { RouteEffects } from './route-effects';
import { legalSlugs } from '@shared/content-schemas';
import { routeMetaFor } from '../routes';
import { ReferralContext, useRememberedReferral } from '../lib/referral-context';

/** Primary navigation, in order. Paths are unprefixed; the router's basename adds `/es`. */
const NAV_ITEMS = [
  { to: '/product', key: 'nav.product' },
  { to: '/docs', key: 'nav.docs' },
  { to: '/faq', key: 'nav.faq' },
  { to: '/contact', key: 'nav.contact' },
] as const;

function AppLink({ href, className, children }: { href: string; className: string; children: string }) {
  // The app lives on another origin when configured; the local fallback is a
  // page on this site and goes through the router.
  if (href.startsWith('http')) {
    return (
      <a className={className} href={href}>
        {children}
      </a>
    );
  }
  const { pathname } = localeFromPathname(href);
  return (
    <SiteLink className={className} to={pathname}>
      {children}
    </SiteLink>
  );
}

/**
 * Whether the current URL is one of the site's pages, or the 404.
 *
 * Static Assets serves the HTML prerendered at /404 for every unknown URL, so
 * on a 404 the server rendered "/404" while the browser hydrates at the real
 * URL. Nothing in the layout may depend on that path, or hydration would see
 * different markup: on a 404 the language switch goes to the other language's
 * home page and no navigation item is marked current.
 */
function useIsKnownPage(locale: Locale): boolean {
  // useLocation() is relative to the basename, so this is the unprefixed path.
  return routeMetaFor(localizePath(useLocation().pathname, locale)) !== undefined;
}

function LanguageSwitch({ locale, known }: { locale: Locale; known: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const other = t('language.switchToLang') as Locale;
  const hydrated = useHydrated();
  const path = localizePath(location.pathname, locale);
  // After hydration the switch keeps the visitor's other query parameters and
  // #fragment; the server HTML (which knows neither) links to the bare page.
  const href = !known
    ? explicitLanguagePath('/', other)
    : hydrated
      ? languageSwitchHref(path, other, location.search, location.hash)
      : explicitLanguagePath(path, other);
  return (
    // The title is in the page's language; only the label (the other language's
    // own name, e.g. "English") carries the other language's lang attribute.
    <a className="language-switch" href={href} hrefLang={other} title={t('language.switchToDescription')}>
      <span lang={other}>{t('language.switchTo')}</span>
    </a>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const locale = useUrlLocale();
  const settings = getSettings(locale);
  const referral = useRememberedReferral();
  const links = appLinks(buildConfig, locale, referral);
  const known = useIsKnownPage(locale);

  return (
    <ReferralContext.Provider value={referral}>
      <LanguageQueryProvider>
        <RouteEffects />
        <a className="skip-link" href="#main">
          {t('a11y.skipToContent')}
        </a>
        <header className="site-header">
          <div className="container header-inner">
            <SiteLink to="/" className="brand-link" aria-label={t('a11y.homeLink')}>
              <Wordmark />
            </SiteLink>
            <nav aria-label={t('a11y.mainNavigation')} className="main-nav">
              <ul>
                {NAV_ITEMS.map((item) => (
                  <li key={item.to}>
                    {known ? (
                      <SiteNavLink to={item.to}>{t(item.key)}</SiteNavLink>
                    ) : (
                      <SiteLink to={item.to}>{t(item.key)}</SiteLink>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
            <div className="header-actions">
              <LanguageSwitch locale={locale} known={known} />
              <AppLink href={links.login} className="button button-quiet">
                {t('nav.signIn')}
              </AppLink>
              <AppLink href={links.signup} className="button button-primary">
                {t('nav.getStarted')}
              </AppLink>
            </div>
          </div>
        </header>
        <div className="status-banner" role="note">
          <div className="container">
            <strong>{t('status.earlyDevelopment')}:</strong> {settings.statusNotice}
          </div>
        </div>
        <main id="main" tabIndex={-1}>
          <Outlet />
        </main>
        <footer className="site-footer">
          <div className="container footer-inner">
            <nav aria-label={t('a11y.footerNavigation')}>
              <ul className="footer-links">
                <li>
                  <SiteLink to="/">{t('nav.home')}</SiteLink>
                </li>
                {NAV_ITEMS.map((item) => (
                  <li key={item.to}>
                    <SiteLink to={item.to}>{t(item.key)}</SiteLink>
                  </li>
                ))}
                <li>
                  <AppLink href={links.signup} className="">
                    {t('nav.getStarted')}
                  </AppLink>
                </li>
              </ul>
            </nav>
            <nav aria-label={t('legal.heading')}>
              <ul className="footer-links">
                {legalSlugs.map((slug) => (
                  <li key={slug}>
                    <SiteLink to={`/${slug}`}>{t(`legal.pages.${slug}`)}</SiteLink>
                  </li>
                ))}
              </ul>
            </nav>
            <p>{settings.footerNote}</p>
            <p>{t('footer.cookies')}</p>
          </div>
        </footer>
      </LanguageQueryProvider>
    </ReferralContext.Provider>
  );
}
